import { useState } from 'react';
import classNames from 'classnames';

// Components
import { CardLayout } from '@components/CardLayout/ui/CardLayout';
import { Button } from '@shared/components';
import RemoveCardModal from '@features/RemoveCardModal/ui/RemoveCardModal';

// Styles
import { deletePaymentMethod } from '@api/user/apiService';
import { BrandType } from '@static/staticData.ts';
import useToast from '@hooks/useToast';
import { PaymentMethod } from '@app/types/payment';

import styles from './index.module.scss';

interface CreditCardItemProps {
  card: PaymentMethod;
  onClick: () => void;
  onDelete: () => void;
  isDefault: boolean;
}

function CreditCardItem(props: CreditCardItemProps) {
  const { card, onClick, isDefault, onDelete } = props;
  const { showSuccess, showError } = useToast();
  const [removeCardModalOpened, setRemoveCardModalOpened] = useState(false);
  const title = `**** **** **** ${card.last4}`;
  const description = `Expires ${card.exp_month}/${card.exp_year}`;

  const handleSetRemoveCardModalOpened = (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    setRemoveCardModalOpened(true);
  };

  return (
    <>
      <CardLayout onClick={onClick} active={isDefault}>
        <div
          className={classNames(styles.image, {
            [styles.activeImg]: isDefault,
          })}
        >
          {card.brand === BrandType.Visa && <img src="/visa.svg" alt="" />}
          {card.brand === BrandType.MasterCard && (
            <img src="/mastercard.svg" alt="" />
          )}
          {card.brand === BrandType.Discover && (
            <img src="/discover.svg" alt="" />
          )}
          {card.brand === BrandType.AmericanExpress && (
            <img src="/amex.svg" alt="" />
          )}
          {card.brand === BrandType.DinersClub && (
            <img src="/diners.svg" alt="" />
          )}
        </div>
        <div className={styles.text}>
          <p className={styles.title}>{title}</p>
          <p className={styles.description}>{description}</p>
          {!isDefault && (
            <Button
              onClick={(e) => handleSetRemoveCardModalOpened(e)}
              className={styles.deleteBtn}
              variant="underline"
            >
              Delete
            </Button>
          )}
        </div>
        <div
          className={classNames(styles.select, { [styles.active]: isDefault })}
        >
          <span>
            {isDefault && <img src="/icons/check.svg" alt="Checked" />}
          </span>
        </div>
      </CardLayout>
      {removeCardModalOpened && (
        <RemoveCardModal
          cardNumber={title}
          expiryDate={description}
          showModal={removeCardModalOpened}
          onCancel={() => setRemoveCardModalOpened(false)}
          onConfirm={() => {
            deletePaymentMethod(
              card.id,
              () => {
                setRemoveCardModalOpened(false);
                showSuccess('Success', 'Your payment method has been removed');
                onDelete();
              },
              (error) => {
                setRemoveCardModalOpened(false);
                showError('Error', error.response.data.error);
              }
            );
          }}
        />
      )}
    </>
  );
}

export default CreditCardItem;
